import type {
  CmmdCreateUserForgeAutomationRuleParams,
  CmmdUserForgeAutomationRule,
} from "@cmmd-forge/contracts";
import { useNavigate } from "@tanstack/react-router";

import { useFeatureFlag } from "~/hooks/useFeatureFlag";
import { AutomationFormLayout } from "./AutomationFormLayout";
import {
  blankAutomationDraft,
  cronFromDraft,
  scheduleDescriptionFromDraft,
  type AutomationDraft,
  type AutomationProjectOption,
} from "./automationFormModel";
import type { CreateLocalAutomationInput } from "./localAutomationClient";
import { useForgeAutomationsIndex } from "./useForgeAutomationsIndex";
import {
  automationResponseMatchesSelection,
  validateAutomationModelSelection,
} from "./useAutomationProviderSelection";

async function confirmCreatedModelSelection(input: {
  readonly created: CmmdUserForgeAutomationRule;
  readonly draft: AutomationDraft;
  readonly reconcile: ReturnType<typeof useForgeAutomationsIndex>["reconcileCloudAutomation"];
}): Promise<boolean> {
  if (!input.draft.modelSelection) return false;
  const confirmed = automationResponseMatchesSelection({
    expectedId: input.created.id,
    expectedProjectId: Number(input.draft.selectedProjectId),
    response: input.created,
    selection: input.draft.modelSelection,
  })
    ? input.created
    : await input.reconcile(input.created.id);
  return automationResponseMatchesSelection({
    expectedId: input.created.id,
    expectedProjectId: Number(input.draft.selectedProjectId),
    response: confirmed ?? null,
    selection: input.draft.modelSelection,
  });
}

async function navigateAfterPersistedCreate(input: {
  readonly automationId: string;
  readonly navigate: ReturnType<typeof useNavigate>;
}) {
  try {
    await input.navigate({
      to: "/automations/$automationId",
      params: { automationId: input.automationId },
    });
    return null;
  } catch {
    return {
      reconciliationRequired: true as const,
      recoveryAutomationId: input.automationId,
      error: `The automation was saved as ${input.automationId}, but Forge could not open it. To prevent a duplicate, return to Automations and open that exact record.`,
    };
  }
}

function githubRepoSlug(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.match(/github\.com[/:]([^/]+\/[^/]+?)(?:\.git)?\/?$/i)?.[1] ?? null;
}

function projectOptions(
  state: ReturnType<typeof useForgeAutomationsIndex>,
): readonly AutomationProjectOption[] {
  return state.projects.map((project) => ({
    id: String(project.id),
    name: project.title,
    githubRepo: githubRepoSlug(project.githubRepoUrl),
  }));
}

function cloudCreateInput(draft: AutomationDraft): CmmdCreateUserForgeAutomationRuleParams {
  return {
    projectId: Number(draft.selectedProjectId),
    presetKey: draft.presetKey,
    name: draft.name.trim(),
    ...(draft.modelSelection
      ? {
          modelSelection: {
            provider: draft.modelSelection.provider,
            model: draft.modelSelection.model,
          },
        }
      : {}),
    prompt: draft.prompt.trim(),
    cronExpression: cronFromDraft(draft),
    scheduleDescription: scheduleDescriptionFromDraft(draft),
    timezone: draft.timezone,
    requirePrReview: draft.requirePrReview,
  };
}

function localCreateInput(
  draft: AutomationDraft,
  projects: readonly AutomationProjectOption[],
): CreateLocalAutomationInput | null {
  if (draft.scheduleKind === "custom") return null;
  const project = projects.find((option) => option.id === draft.selectedProjectId);
  const hasClockTime = draft.scheduleKind === "daily" || draft.scheduleKind === "weekly";
  return {
    name: draft.name.trim(),
    prompt: draft.prompt.trim(),
    scheduleKind: draft.scheduleKind,
    scheduleTime: hasClockTime ? draft.scheduleTime : null,
    scheduleDayOfWeek: draft.scheduleKind === "weekly" ? draft.scheduleDayOfWeek : null,
    timezone: draft.timezone,
    projectName: project?.name ?? null,
    model: null,
  };
}

export function ForgeAutomationCreatePage() {
  const state = useForgeAutomationsIndex();
  const navigate = useNavigate();
  const everyMinuteAvailable = useFeatureFlag("cmmd_automations_every_minute");
  const projects = projectOptions(state);

  async function create(draft: AutomationDraft) {
    if (draft.runLocation === "cloud") {
      if (!(await validateAutomationModelSelection(draft))) {
        return "That provider or model is no longer available for this project. Choose an available model and try again.";
      }
      const result = await state.createCloudAutomation(cloudCreateInput(draft));
      if (result.status === "rejected") return null;
      if (result.status === "persistence-unknown") {
        return {
          reconciliationRequired: true as const,
          error:
            "CMMD may have saved this automation, but the response was interrupted. To prevent a duplicate, return to Automations and refresh before trying again.",
        };
      }
      const created = result.automation;
      if (!draft.modelSelection) return null;
      if (
        await confirmCreatedModelSelection({
          created,
          draft,
          reconcile: state.reconcileCloudAutomation,
        })
      ) {
        return navigateAfterPersistedCreate({
          automationId: `cloud-${created.id}`,
          navigate,
        });
      }
      return {
        reconciliationRequired: true as const,
        recoveryAutomationId: `cloud-${created.id}`,
        error:
          "The automation may have been saved, but its provider and model could not be confirmed. To prevent a duplicate, return to Automations and refresh before trying again.",
      };
    }
    const input = localCreateInput(draft, projects);
    if (!input) return "Choose a supported schedule before creating this automation.";
    const created = await state.createLocalAutomation(input);
    if (created) {
      return navigateAfterPersistedCreate({
        automationId: `local-${created.id}`,
        navigate,
      });
    }
    return null;
  }

  return (
    <AutomationFormLayout
      busy={
        state.updatingAutomationKey === "cloud:create" ||
        state.updatingAutomationKey === "local:create"
      }
      error={state.error}
      everyMinuteAvailable={everyMinuteAvailable}
      initialDraft={blankAutomationDraft(projects)}
      localExecutionAvailable={state.localExecutionAvailable}
      mode="create"
      projects={projects}
      targetLocked={false}
      onSubmit={create}
    />
  );
}
